export const POPUP_WINDOW_DAYS = 90
export const ZURICH_TIME_ZONE = 'Europe/Zurich'
export const EVENT_POPUP_STORAGE_KEY = 'moveland.eventPopupShown'

const DAY_MS = 24 * 60 * 60 * 1000
const EXCLUDED_PREFIXES = ['/anmeldung', '/login', '/admin']

let memoryShown = false

export function clearEventPopupSession() {
  memoryShown = false
  try {
    window.sessionStorage.removeItem(EVENT_POPUP_STORAGE_KEY)
  } catch {
    memoryShown = false
  }
}

export function hasShownEventPopup() {
  if (memoryShown) return true
  try {
    return window.sessionStorage.getItem(EVENT_POPUP_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function markEventPopupShown() {
  memoryShown = true
  try {
    window.sessionStorage.setItem(EVENT_POPUP_STORAGE_KEY, '1')
  } catch {
    memoryShown = true
  }
}

export function canShowEventPopup(pathname, search = '') {
  const path = pathname || '/'
  if (EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return false
  const query = search.startsWith('?') ? search.slice(1) : search
  if (path === '/events' && new URLSearchParams(query).has('event')) return false
  return true
}

export function eventDetailPath(event) {
  return `/events?event=${encodeURIComponent(event.id)}`
}

function startTime(event) {
  const time = Date.parse(event?.start_at || '')
  return Number.isFinite(time) ? time : NaN
}

export function isEligibleEvent(event, now) {
  if (!event || event.status !== 'published') return false
  const start = startTime(event)
  return Number.isFinite(start) && start > now
}

export function upcomingEvents(events, now) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => isEligibleEvent(event, now))
    .sort((a, b) => startTime(a) - startTime(b) || Number(a.id) - Number(b.id))
}

export function nextEvent(events, now) {
  return upcomingEvents(events, now)[0] || null
}

export function popupEvents(events, now) {
  const limit = now + POPUP_WINDOW_DAYS * DAY_MS
  return upcomingEvents(events, now).filter((event) => startTime(event) <= limit)
}

export function resolveBannerEvent(events, config, now) {
  if (!config?.banner_enabled) return null
  if (config.banner_mode === 'manual') {
    const selected = (Array.isArray(events) ? events : []).find((event) => String(event.id) === String(config.banner_event_id))
    return isEligibleEvent(selected, now) ? selected : null
  }
  return nextEvent(events, now)
}

export function resolvePopupEvents(events, config, now) {
  if (!config?.popup_enabled) return []
  if (config.popup_mode === 'manual') {
    const byId = new Map((Array.isArray(events) ? events : []).map((event) => [String(event.id), event]))
    return (config.popup_event_ids || [])
      .map((id) => byId.get(String(id)))
      .filter((event) => isEligibleEvent(event, now))
  }
  return popupEvents(events, now)
}

export function countdownParts(startAt, now) {
  const start = Date.parse(startAt || '')
  const diff = Number.isFinite(start) ? Math.max(0, start - now) : 0
  return {
    days: Math.floor(diff / DAY_MS),
    hours: Math.floor((diff % DAY_MS) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
  }
}

export function countdownLabel(parts) {
  const days = Number(parts?.days) || 0
  const hours = String(Number(parts?.hours) || 0).padStart(2, '0')
  const minutes = String(Number(parts?.minutes) || 0).padStart(2, '0')
  return `${days} ${days === 1 ? 'Tag' : 'Tage'} · ${hours} Std. · ${minutes} Min.`
}

export function zurichParts(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZURICH_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const value = (type) => parts.find((part) => part.type === type)?.value || ''
  return { year: value('year'), month: value('month'), day: value('day'), hour: value('hour'), minute: value('minute') }
}

function zurichFormat(iso, options) {
  const date = new Date(iso)
  if (!iso || Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('de-CH', { timeZone: ZURICH_TIME_ZONE, ...options }).format(date)
}

export function formatZurichDate(iso) {
  return zurichFormat(iso, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

export function formatZurichTime(iso) {
  return zurichFormat(iso, { hour: '2-digit', minute: '2-digit' })
}

export function formatZurichDateTime(iso) {
  return zurichFormat(iso, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function registrationAction(event) {
  if (event?.registration_action === 'external' || event?.registration_action === 'none' || event?.registration_action === 'moveland') {
    return event.registration_action
  }
  return 'moveland'
}

export function externalCta(event) {
  if (!event?.external_url) return ''
  return registrationAction(event) === 'external' ? 'Beim Veranstalter anmelden' : 'Mehr beim Veranstalter'
}

export function registrationStatus(event, now = Date.now()) {
  const action = registrationAction(event)
  if (action === 'external') return { tone: 'external', label: 'Externe Anmeldung' }
  if (action === 'none') return { tone: 'none', label: 'Keine Anmeldung nötig' }
  if (!event || typeof event.is_full !== 'boolean' || typeof event.registration_open !== 'boolean') {
    return { tone: 'unknown', label: 'Anmeldeinfos ansehen' }
  }
  if (event.is_full) return { tone: 'full', label: 'Ausgebucht' }
  const opens = event.registration_opens_at || event.registration_opens || ''
  const opensAt = Date.parse(opens)
  if (event.registration_open === false && Number.isFinite(opensAt) && opensAt > now) {
    return { tone: 'soon', label: `Anmeldung startet am ${formatZurichDateTime(opens)}` }
  }
  if (event.registration_open === false || event.registration_enabled === false) {
    return { tone: 'closed', label: 'Anmeldung geschlossen' }
  }
  return { tone: 'open', label: 'Anmeldung offen' }
}

export function deadlineNote(event) {
  if (!event?.registration_deadline) return ''
  if (typeof event.registration_open !== 'boolean') return ''
  const when = formatZurichDateTime(event.registration_deadline)
  return when ? `Anmeldeschluss ${when}` : ''
}

export function availabilityNote(event) {
  if (event?.capacity == null || event?.spots_left == null) return ''
  const left = Number(event.spots_left)
  const capacity = Number(event.capacity)
  if (!Number.isFinite(left) || !Number.isFinite(capacity) || left <= 0) return ''
  return `${left} von ${capacity} Plätzen frei`
}

export function waitlistNote(event) {
  if (registrationAction(event) !== 'moveland') return ''
  if (event?.is_full && event?.waitlist_enabled && event?.registration_open) return 'Warteliste verfügbar'
  return ''
}

export function formatPrice(raw) {
  if (raw == null) return ''
  const trimmed = String(raw).trim()
  if (!trimmed) return ''
  if (trimmed.toLowerCase() === 'kostenlos') return 'Kostenlos'
  if (/chf/i.test(trimmed)) return trimmed
  const amount = Number.parseFloat(trimmed.replace(',', '.'))
  if (!Number.isFinite(amount)) return trimmed
  return `${amount.toFixed(2)} CHF`
}

export function descriptionBlocks(text) {
  return String(text || '')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
}

export function discoveryBlocked() {
  if (typeof document === 'undefined') return false
  if (document.querySelector('[role="dialog"], .popup-overlay')) return true
  const active = document.activeElement
  if (!active || active === document.body) return false
  const tag = active.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable
}
